import React, { useState, useEffect, useRef, useCallback } from "react";
import L from "leaflet";
import { FaCircleInfo } from "react-icons/fa6";

const Information = () => {
  const [open, setOpen] = useState(false);
  const infoRef = useRef(null);
  const infoPanelRef = useCallback((node) => {
    infoRef.current = node;
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);
  // Leaflet's own drag/click handling swallows clicks on overlay controls
  // unless propagation to the map is explicitly disabled on the DOM node.
  const btnRef = useCallback((node) => {
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (infoRef.current && !infoRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // Content for the Info component, combining information from both images
  const infoContent = (
    <div style={{ lineHeight: '1.4' }}>
      <h3 style={{ fontSize: '1.1em', marginBottom: '8px', fontWeight: 'bold' }}>About NASA FIRMS Data</h3>
      <p style={{ fontSize: '0.9em' }}>
        The Fire Information for Resource Management System (FIRMS) provides near real-time fire detection data from NASA’s Visible Infrared Imaging Radiometer Suite (VIIRS) satellites. The data is updated every 3 hours.
      </p>

      <hr style={{ margin: '20px 0', borderColor: '#eee' }} />

      <h4 style={{ fontSize: '1em', margin: '0 0 10px 0', fontWeight: 'bold' }}>Attribute Fields for VIIRS 375m Active Fire Data</h4>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
        <thead>
          <tr style={{ backgroundColor: '#f2f2f2' }}>
            <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Attribute</th>
            <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>Latitude</td>
            <td style={{ padding: '8px', border: '1px solid #ddd' }}>Center of nominal 375 m fire pixel.</td>
          </tr>
          <tr>
            <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>Longitude</td>
            <td style={{ padding: '8px', border: '1px solid #ddd' }}>Center of nominal 375 m fire pixel.</td>
          </tr>
          <tr>
            <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>Bright_ti4</td>
            <td style={{ padding: '8px', border: '1px solid #ddd' }}>VIIRS I-4 channel brightness temperature of the fire pixel measured in Kelvin.</td>
          </tr>
          <tr>
            <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>Bright_ti5</td>
            <td style={{ padding: '8px', border: '1px solid #ddd' }}>VIIRS I-5 channel brightness temperature of the fire pixel measured in Kelvin.</td>
          </tr>
          <tr>
            <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>Acq_Date</td>
            <td style={{ padding: '8px', border: '1px solid #ddd' }}>Date of VIIRS acquisition.</td>
          </tr>
          <tr>
            <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>Acq_Time</td>
            <td style={{ padding: '8px', border: '1px solid #ddd' }}>Time of acquisition/overpass of the satellite (in UTC).</td>
          </tr>
          <tr>
            <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>Confidence</td>
            <td style={{ padding: '8px', border: '1px solid #ddd' }}>Confidence values are set to low, nominal, and high based on a collection of intermediate algorithm quantities.</td>
          </tr>
          <tr>
            <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>FRP</td>
            <td style={{ padding: '8px', border: '1px solid #ddd' }}>Fire Radiative Power (MW). Depicts the pixel-integrated fire radiative power.</td>
          </tr>
        </tbody>
      </table>

      <p style={{ marginTop: '15px', fontSize: '0.85em' }}>
        <strong>Source:</strong>{" "}
        <a href="https://www.earthdata.nasa.gov/data/tools/firms/active-fire-data-attributes-modis-viirs" target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
          NASA FIRMS Attribute Fields
        </a>
      </p>
    </div>
  );

  return (
    <div style={{ position: "absolute", bottom: "20px", left: "12px", zIndex: 1000 }}>
      {/* Info Button */}
      <button
        ref={btnRef}
        onClick={() => setOpen(!open)}
        title="More Info"
        style={{
          background: "rgba(255, 255, 255, 0.15)",
          backdropFilter: "blur(10px) brightness(1.1)",
          WebkitBackdropFilter: "blur(10px) brightness(1.1)",
          border: "1px solid rgba(255, 255, 255, 0.25)",
          borderRadius: "8px",
          padding: "8px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          fontSize: 16,
          color: "white",
          transition: "transform 0.2s",
        }}
        aria-label="Show info"
        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1.0)")}
      >
        <FaCircleInfo />
      </button>

      {/* Info Card */}
      {open && (
        <div
          ref={infoPanelRef}
          style={{
            position: "absolute",
            bottom: 30,
            left: 0,
            minWidth: 300,
            maxWidth: 450,
            maxHeight: 350,
            overflowY: 'auto',
            background: "#fff",
            borderRadius: 12,
            boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
            padding: "16px 18px",
            fontSize: 13,
            color: "#333",
            zIndex: 1000,
            animation: "fadeIn 0.3s ease",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <h5 style={{ fontWeight: 600, color: "#000" }}>Information</h5>
            <button
              onClick={() => setOpen(false)}
              style={{
                background: "none",
                border: "none",
                fontSize: 18,
                cursor: "pointer",
                color: "#888",
              }}
              aria-label="Close info"
            >
              ×
            </button>
          </div>
          <div>{infoContent}</div>
        </div>
      )}

      {/* Fade-in Animation (consider moving to a CSS file for global styles) */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Information;